<script setup>
import { ref, watch } from 'vue'
import useApi from '../../Services/useApi'
import useNotification from '../../Services/useNotification'

const props = defineProps(['item', 'index'])
const emit = defineEmits(['deleted', 'updated'])
const api = useApi()
const notificate = useNotification()
const expanded = ref(false)
const saving = ref(false)
const errors = ref({})

const dateValue = value => value ? String(value).slice(0, 10) : ''
const form = ref({
    bday: dateValue(props.item.bday),
    variety: props.item.variety || '',
    observations: props.item.observations || '',
})

watch(() => props.item, item => {
    form.value = {
        bday: dateValue(item.bday),
        variety: item.variety || '',
        observations: item.observations || '',
    }
}, { deep: true })

const save = async () => {
    saving.value = true
    errors.value = {}

    try {
        const response = await api.updateTree(props.item.id, form.value)
        emit('updated', response.data.tree)
        notificate.success('Información del punto guardada')
    } catch (error) {
        errors.value = error.response?.data?.errors || {}
        notificate.error('No fue posible guardar la información')
    } finally {
        saving.value = false
    }
}

const remove = () => {
    notificate.confirm(`Eliminar ${props.item.name}. Esta acción no se puede deshacer.`, async () => {
        try {
            const response = await api.deleteTree(props.item.id)
            emit('deleted', {
                treeId: response.data.tree_id,
                harvestSize: response.data.harvest_size,
            })
            notificate.success('Punto eliminado')
        } catch (error) {
            notificate.error('No fue posible eliminar el punto')
        }
    })
}
</script>

<template>
    <article class="w-full my-2 overflow-hidden transition-all shadow-md rounded-3xl bg-light_yellow hover:shadow-lg">
        <button
            type="button"
            class="flex items-center w-full px-4 py-3 text-left md:px-5"
            :aria-expanded="expanded"
            @click="expanded = !expanded"
        >
            <span class="flex items-center justify-center flex-shrink-0 w-9 h-9 mr-3 font-bold text-white rounded-full bg-enriqueto_green">
                {{index + 1}}
            </span>
            <img
                class="object-cover flex-shrink-0 w-12 h-12 mr-4 bg-white border-2 rounded-full border-enriqueto_green"
                :src="item.fruit.image"
                :alt="item.fruit.name"
                width="48"
                height="48"
                loading="lazy"
                decoding="async"
            />
            <span class="flex-1 min-w-0">
                <strong class="block text-enriqueto_green">{{item.name}}</strong>
                <small class="block text-gray-700">{{item.fruit.name}} · {{item.lat}} / {{item.lon}}</small>
            </span>
            <span class="ml-3 text-2xl text-enriqueto_green" aria-hidden="true">
                {{expanded ? '−' : '+'}}
            </span>
        </button>

        <div v-if="expanded" class="px-4 pb-5 border-t border-light_green md:px-6">
            <div class="grid gap-4 pt-5 md:grid-cols-2">
                <label class="block">
                    <span class="block mb-2 font-bold text-enriqueto_green">Fecha de siembra</span>
                    <input
                        v-model="form.bday"
                        type="date"
                        class="w-full p-3 bg-white border rounded-xl border-light_green"
                    />
                    <small v-if="errors.bday" class="block mt-1 text-red">{{errors.bday[0]}}</small>
                </label>
                <label class="block">
                    <span class="block mb-2 font-bold text-enriqueto_green">Variedad</span>
                    <input
                        v-model="form.variety"
                        type="text"
                        maxlength="255"
                        placeholder="Ej. Criollo, CCN-51"
                        class="w-full p-3 bg-white border rounded-xl border-light_green"
                    />
                    <small v-if="errors.variety" class="block mt-1 text-red">{{errors.variety[0]}}</small>
                </label>
            </div>

            <label class="block mt-4">
                <span class="block mb-2 font-bold text-enriqueto_green">Observaciones</span>
                <textarea
                    v-model="form.observations"
                    rows="4"
                    maxlength="5000"
                    placeholder="Escribe aquí cualquier observación sobre este punto"
                    class="w-full p-3 bg-white border resize-y rounded-xl border-light_green"
                ></textarea>
                <small v-if="errors.observations" class="block mt-1 text-red">{{errors.observations[0]}}</small>
            </label>

            <div class="flex flex-col gap-3 mt-5 sm:flex-row sm:justify-end">
                <button
                    type="button"
                    class="px-5 py-3 font-bold bg-white border-2 rounded-xl text-red border-red hover:bg-red-50"
                    @click="remove"
                >
                    Eliminar punto
                </button>
                <button
                    type="button"
                    class="px-5 py-3 font-bold text-white rounded-xl bg-enriqueto_green disabled:opacity-60"
                    :disabled="saving"
                    @click="save"
                >
                    {{saving ? 'Guardando…' : 'Guardar información'}}
                </button>
            </div>
        </div>
    </article>
</template>
